/* eslint-disable @typescript-eslint/no-explicit-any */
import { Api } from "../../services/interceptor";

/**
 * Player-profile endpoints used by the desktop profile page.
 * Same backend routes the mobile app uses (see CricCenterClient app_endpoints.dart); the page-specific
 * shaping lives in model.ts so a dedicated website endpoint can replace these without touching the UI.
 */
const unwrap = <T,>(payload: unknown): T =>
  (payload && typeof payload === "object" && "data" in (payload as object) ? (payload as { data: T }).data : payload) as T;

export const getProfile = async (id: string) => (await Api.get(`/getPlayerProfile/${id}`)).data as any;
export const getOverview = async (id: string) => unwrap<any>((await Api.get(`/player/${id}/profile-overview`)).data);
export const getBattingAnalysis = async (id: string) => unwrap<any>((await Api.get(`/player/${id}/profile-batting`)).data);
export const getBowlingAnalysis = async (id: string) => unwrap<any>((await Api.get(`/player/${id}/bowling-analysis`)).data);
export const getFieldingAnalysis = async (id: string) => unwrap<any>((await Api.get(`/player/${id}/fielding-analysis`)).data);
export const getSpotlight = async (id: string) => ((await Api.get(`/playerSpotlight`, { params: { playerId: id } })).data ?? []) as any[];
export const getTeams = async (id: string) => ((await Api.get(`/teamsByPlayerId`, { params: { playerId: id } })).data ?? []) as any[];
/** Set once the backend answers 404/405 for the lightweight endpoint (older deployment) so we stop trying it. */
let matchSummaryUnavailable = false;

/**
 * A player's matches. Uses the lightweight summary endpoint (slim cards + `playerResult` W/L/T/D/NR for this player) and falls
 * back to the full endpoint if the backend does not have it yet (no `playerResult` there; the pane then falls back to text).
 */
export const getMatches = async (id: string, status: "ENDED" | "IN_PROGRESS" | "SCHEDULED", page: number, size = 10) => {
  if (!matchSummaryUnavailable) {
    try {
      return ((await Api.get(`/getRelevantMatchesSummaryPageable`, { params: { status, playerId: id, page, size } })).data ?? {}) as { matchResponseList?: any[] };
    } catch (e) {
      const code = (e as { response?: { status?: number } })?.response?.status;
      if (code !== 404 && code !== 405) throw e;
      matchSummaryUnavailable = true;
    }
  }
  return ((await Api.get(`/getRelevantMatchesPageable`, { params: { status, playerId: id, searchFollowingLeagues: false, page, size } })).data ?? {}) as { matchResponseList?: any[] };
};
